import { useState } from "react";
import { FiHome, FiTag, FiRepeat, FiChevronRight } from "react-icons/fi";
import { useNavigate, useLocation } from "react-router-dom";

// design icons
import hire360Logo from "../design-icons/hire360-logo.png";
import leftArrowIcon from "../design-icons/arrow-left.svg";
import homeIcon from "../design-icons/home.svg";

import requisitionsIcon from "../design-icons/requisitions.svg";
import cadidatePipelineIcon from "../design-icons/candidate-pipeline.svg";
import OffersIcon from "../design-icons/file-lines.svg";
import complianceIcon from "../design-icons/file-circle-check.svg";
import journeyIcon from "../design-icons/journey.svg";
import myTEIcon from "../design-icons/clock.svg";
import fastTrackIcon from "../design-icons/forward-fast.svg";
import insightsIcon from "../design-icons/lightbulb.svg";

import chatIcon from "../design-icons/chat-bubble.svg";
import xIcon from "../design-icons/x.svg";
import tryItOut from "../design-icons/arrow-forward.svg";

import settingsIcon from "../design-icons/settings.svg";
import logoutIcon from "../design-icons/logout.svg";

import footerLogo from "../design-icons/logo-4a.png";

import { AIChatTrigger } from "./AIChatDrawer";
import { useChat } from "../store/ChatContext";

const ICON_CLASS = "w-5 h-5 flex-shrink-0";
const ACTIVE_FILTER =
  "invert(56%) sepia(62%) saturate(520%) hue-rotate(127deg) brightness(93%) contrast(89%)";

const Sidebar = ({ isAuthenticated, availableApps = [], onLogout, user }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebar-collapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  // const [activeItem, setActiveItem] = useState("Home");
  const [showAssistant, setShowAssistant] = useState(true); 
	const { chatbotEnabled } = useChat()

  const guestNav = [
    { label: "Home", icon: <FiHome />, path: "/" },
    { label: "Features", icon: <FiTag />, path: "/features" },
    { label: "Pricing", icon: <FiRepeat />, path: "/pricing" },
  ];

  const homeNav = [
    { label: "Home", icon: homeIcon, path: "/Hire360/Dashboard" },
  ];

  const recruitNav = [
    {
      label: "Requisitions",
      icon: requisitionsIcon,
      path: "/Hire360/launch-attract",
    },
    {
      label: "Candidate Pipeline",
      icon: cadidatePipelineIcon,
      path: "/Hire360/screen-select",
    },
    { label: "Offers", icon: OffersIcon, path: "/Hire360/decide-offer" },
  ];

  const onboardNav = [
    {
      label: "Compliance",
      icon: complianceIcon,
      path: "/Hire360/verify-comply",
    },
    {
      label: "Journey",
      icon: journeyIcon,
      path: "/Hire360/welcome-transition",
    },
  ];

  const insightsNav = [
    { label: "Fast Track", icon: fastTrackIcon, path: "/Hire360/skip-line" },
    { label: "Insights", icon: insightsIcon, path: "/Hire360/insights" },
  ];

  const footerNav = [
    // {
    //   label: "Settings",
    //   icon: settingsIcon,
    //   action: null,
    // },
    {
      label: "Logout",
      icon: logoutIcon,
      action: onLogout,
    },
  ];

  const navSections = [
    { title: "", items: homeNav },
    { title: "Recruit", items: recruitNav },
    { title: "Onboard", items: onboardNav },
    { title: "Insights", items: insightsNav },
  ];

  const handleNavClick = (item) => {
    // setActiveItem(item.label);

    if (item.action) item.action();
    else if (item.path) navigate(item.path);
  };

  const TooltipIcon = ({ label, children }) => (
    <div className="relative group">
      {children}
      <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 bg-gray-900 text-white 
      text-xs rounded-md px-2.5 py-1 opacity-0 group-hover:opacity-100 pointer-events-none 
      whitespace-nowrap transition-opacity duration-150 z-[100]">
        {label}
      </span>
    </div>
  );

  const toggleCollapsed = (value) => {
    setCollapsed(value);
    localStorage.setItem("sidebar-collapsed", JSON.stringify(value));
  };

  // --- Unauthenticated (guest) sidebar ---
  if (!isAuthenticated)
    return (
      <aside className="w-48 bg-transparent bg-white border-b border-slate-200 shadow-sm">
        <div className="px-5 pt-6 pb-3">
          <h1 className="text-xl font-semibold text-[#092162]">Hire360</h1>
        </div>
        <nav className="px-2 mt-2 text-gray-700 text-sm flex-1 overflow-y-auto">
          {guestNav.map((item, idx) => (
            <button
              key={idx}
              onClick={() => navigate(item.path)}
              className="flex items-center gap-2 w-full text-left rounded-md hover:bg-gray-100 transition-colors py-1 px-2"
            >
              <span className="text-[#092162] text-sm">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>
    );

  // --- Authenticated Sidebar ---

  return (
		<>
			{collapsed ? (
					<aside className="w-24 flex flex-col min-h-screen bg-white rounded-[10px] border-r border-gray-200 shadow-sm">
						{/* Logo + Expand Button */}
						<div className="flex flex-col items-center pt-5 pb-2">
							<img src={hire360Logo} alt="Hire360 Logo" className="w-10 h-9" />
							<button
								onClick={() => toggleCollapsed(false)}
								className="mt-2 p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
								aria-label="Expand sidebar"
							>
								<FiChevronRight size={16} />
							</button>
						</div>

						{/* Nav Icons */}
						<nav className="flex flex-col px-2 mt-1 text-sm" style={{ maxHeight: "75%" }}>
							<div className="flex-1 ">
								{navSections.map((section, sIdx) => (
									<div key={sIdx} className={sIdx === 0 ? "mb-6" : "mb-4"}>
										{section.title && (
											<p className="text-sm font-normal text-zinc-400 text-center mb-1.5 mt-1">
												{section.title}
											</p>
										)}
										{section.items.map((item, idx) => {
											const isActive = item.path === location.pathname;
											return (
												<TooltipIcon key={idx} label={item.label}>
													<button
														onClick={() => handleNavClick(item)}
														className={`flex items-center justify-center w-full rounded-lg text-zinc-600 hover:bg-gray-100 py-2 transition-colors`}
														aria-label={item.label}
													>
														<img
															src={item.icon}
															alt={item.label}
															className={ICON_CLASS}
															style={isActive ? { filter: ACTIVE_FILTER } : undefined}
														/>
													</button>
												</TooltipIcon>
											);
										})}
									</div>
								))}
							</div>
						</nav>

						{/* AI Assistant */}
						<div className="flex-1 p-2 mt-4">
							{chatbotEnabled && (
								<>
									<p className="text-sm font-normal text-zinc-400 text-center mb-1.5 mt-1">
										AI Assistant
									</p>
									<TooltipIcon label="AI Assistant">
										<AIChatTrigger />
									</TooltipIcon>
								</>
							)}
						</div>

						{/* Bottom: Settings/Logout + Company Logo */}
						<div className="px-2 py-2 space-y-0.5">
							
		
							{/* Settings + Logout */}
							{/* <p className="text-sm font-normal text-zinc-400 text-center mb-1 mt-3">
								Settings
							</p> */}
							{footerNav.map((item, idx) => {
								const isActive = item.path === location.pathname;
								return (
									<TooltipIcon key={idx} label={item.label}>
										<button
											onClick={() => handleNavClick(item)}
											className="flex items-center justify-center w-full rounded-lg py-2 text-zinc-600 hover:bg-gray-100 transition-colors"
											aria-label={item.label}
										>
											<img 
												src={item.icon} 
												alt={item.label} 
												className={ICON_CLASS} 
												style={isActive ? { filter: ACTIVE_FILTER } : undefined}
											/>
										</button>
									</TooltipIcon>
								)
							})}
		
							{/* Company Logo — square, contained, centered */}
							<div className="border-t border-stone-200 mt-2 -mx-2"></div>

							<div className="flex justify-center pt-3 my-2">
								<img
									src={footerLogo}
									alt="Company logo"
									className="w-19 h-19 rounded-md object-contain p-1"
								/>
							</div>
						</div>
					</aside>
				) : (
					<aside className="w-50 flex flex-col min-h-screen bg-white rounded-[10px] border-r border-gray-200 shadow-sm">
						{/* Header */}
						<div className="flex items-center justify-between pt-5 pb-3 px-4 mt-2">
							<div className="flex items-center gap-2">
								<img src={hire360Logo} alt="Hire360 Logo" className="w-10 h-9" />
								<h1 className="text-xl font-semibold text-slate-900 justify-start">
									Hire360.
								</h1>
							</div>
							<button
								onClick={() => toggleCollapsed(true)}
								className="flex items-center gap-0 p-1.5 rounded-md hover:bg-gray-100 text-gray-500 transition-colors"
								aria-label="Collapse sidebar"
							>
								<img src={leftArrowIcon} alt="" className="w-3.5 h-3.5" />
								<img src={leftArrowIcon} alt="" className="w-3.5 h-3.5 -ml-1" />
							</button>
						</div>

						{/* Navigation */}
						<nav className="flex flex-col px-3 mt-1 text-sm overflow-y-auto" style={{ maxHeight: "75%" }}>
							<div className="flex-1">
								{navSections.map((section, sIdx) => (
									<div key={sIdx} className={sIdx === 0 ? "mb-6" : "mb-4"}>
										{section.title && (
											<p className="text-sm font-medium text-zinc-400 pl-3 mb-2 mt-2">
												{section.title}
											</p>
										)}

										{section.items.map((item, idx) => {
											const isActive = item.path === location.pathname;

											return (
												<button
													key={idx}
													onClick={() => handleNavClick(item)}
													className={`flex items-center gap-2.5 w-full font-normal text-left rounded-lg py-2 px-3 hover:bg-gray-100 transition-colors
														${section.title ? "pl-7" : "pl-3"}
														${isActive
															? "text-[#14B8A6]"
															: "text-zinc-600"
														}`}
												>
													<img
														src={item.icon}
														alt={item.label}
														className={section.title ? ICON_CLASS : "w-4 h-4 flex-shrink-0"}
														style={isActive ? { filter: ACTIVE_FILTER } : undefined}
													/>
													<span className="truncate">{item.label}</span>
												</button>
											);
										})}
									</div>
								))}
							</div>
						</nav>

						{/* Meet Assistant Card */}
						<div className="flex-1 px-3 mt-2 w-43 overflow-hidden">
							{chatbotEnabled && (
								<div className="mb-3 p-3 bg-gray-50 rounded-xl border border-gray-200 relative overflow-hidden">
									<p className="text-sm font-semibold text-slate-900 tracking-tight mb-1">
										Meet Assistant
									</p>
									<p className="text-xs text-zinc-500 leading-4 tracking-tight font-normal break-words max-w-45">
										Ask anything.
									</p>
									<AIChatTrigger />
								</div>
							)}
						</div>

						{/* Footer: Settings + Logout */}
						<div className="px-3 pb-3 pt-3 space-y-0.5">
							{footerNav.map((item, idx) => {
								const isActive = item.path === location.pathname;
								return (
									<button
										key={idx}
										onClick={() => handleNavClick(item)}
										className={`flex items-center gap-2.5 w-full text-left rounded-lg py-2 pl-3 hover:bg-gray-100 transition-colors
											${isActive
												? "text-[#14B8A6]"
												: "text-zinc-600"
											}`}
									>
										<img
											src={item.icon}
											alt={item.label}
											className={ICON_CLASS}
											style={isActive ? { filter: ACTIVE_FILTER } : undefined}
										/>
										<span className="text-sm">{item.label}</span>
									</button>
								);
							})}
							{/* Footer Logo */}
							<div className="border-t border-stone-200 mt-2 -mx-3"></div>

							<div className="flex justify-center py-2 px-3">
								<img
									src={footerLogo}
									alt="4A logo"
									className="w-20 h-15 object-contain"
								/>
							</div>
						</div>
					</aside>
				) 
			}
		</>
	);
};

export default Sidebar;
